import React from 'react'
import {
  PAYMENT_TOKEN_TYPES,
  PAYMENT_CARDS,
  CREDIT_PROVIDERS,
} from '../../constants'
import {
  INSTRUMENT_TYPES,
  PP_CREDIT_PROMO_STATE,
} from '../../../../../lib/constants'
import enRTRStrings from '../../../../../config/enRTRStrings'
import content from 'pp-react-l10n'
import _, { get } from 'lodash'
import { formatAmount } from '../currencyUtils'
import { getCfpbContentKey } from '../../../lib/contentUtils'
import sourceLogoMap from '../funding-source-logo-map'
import { StyleSheet, css } from 'lib/paypalphrodite'
import * as staticUtils from './staticUtils'
import { isDw2Repaint } from 'experiments/dw2Repaint'

import serverProps from '../serverProps'
import { BodyText } from '@paypalcorp/pp-react'
export * from './staticUtils'

const i18n = content('transfer/inc/fundingOptionDisplayName')

const CDN_HOST_NAME = _.get(serverProps, 'cdnHostName')

const styles = StyleSheet.create({
  externalProviderLogo: {
    width: '38px',
    height: '15px',
  },
  verticleLine: {
    width: '1px',
    display: 'inline-block',
    height: '11px',
    border: '1px solid lightGrey',
    marginLeft: '7px',
    marginRight: '7px',
  },
  marginFixProviderLogo: {
    marginTop: '-5px',
  },
})

// TODO test this
export function buildBalanceToggleDisplayName(
  balanceSource,
  isCFPBEnabledCountry,
  userAccountType
) {
  const {
    total_funds_in: { amount, currency_code: currencyCode },
  } = balanceSource
  const strippedNumber = amount.replace(/[^0-9.,]/g, '')
  const formattedAmount = formatAmount(
    strippedNumber,
    currencyCode,
    'CURRENCY_FORMAT_SYMBOLISOCURRENCY'
  )
  const contentKey = getCfpbContentKey(
    'displayName.balanceToggle',
    isCFPBEnabledCountry,
    userAccountType
  )
  return i18n(contentKey, {
    formattedAmount: isDw2Repaint() ? (
      <BodyText strong style={{ display: 'inline-block' }}>
        {formattedAmount}
      </BodyText>
    ) : (
      <span className="vx_text-5">{formattedAmount}</span>
    ),
  })
}

/**
 * Used as a standardizer for the logo urls on paypal objects.
 */
export function generateSourceLogoUrls(
  source,
  isCFPBEnabledCountry,
  userAccountType,
  isCardArtEnabled
) {
  // if the server returns a logo url, use it
  if (isCardArtEnabled && source.thumbnail_logo_url) {
    return source.thumbnail_logo_url
  }

  let sourceName = getFundingSourceName(
    source,
    isCFPBEnabledCountry,
    userAccountType
  ) // try for something like 'Bank Of America'

  if (typeof sourceName !== 'string') {
    sourceName = ''
  }

  let normalizedSourceName = sourceName
    .replace(/[.,]|\s\(.*\)/g, '')
    .replace(/[\s_]/g, '-')
    .replace(/&#(\d+);/g, '') // To remove numeric html entities
    .toLowerCase()

  // For UK guest flow, visa type is visa/delta/electron
  if (normalizedSourceName === 'visa/delta/electron') {
    normalizedSourceName = 'visa'
  }

  const logoExists = sourceLogoMap[normalizedSourceName]
  if (logoExists) {
    return `${CDN_HOST_NAME}/digitalassets/c/consumer/p2p/funding-sources/${normalizedSourceName}.png`
  }

  // otherwise fallback to the default type logos (generic card, bank, etc. image)
  const instrumentType = source.instrument_type || ''
  const normalizedInstrumentType = instrumentType
    .replace(/[.,]/g, '')
    .replace(/[\s_]/g, '-')
    .toLowerCase()

  return `${CDN_HOST_NAME}/digitalassets/c/consumer/p2p/funding-sources/${normalizedInstrumentType}.png`
}

/**
 * Makes display names for "funding sources" which are actually multiple balance sources bundled together as a single source.
 * The UI of the funding options selector treats this as a single source: "PayPal Balance (USD, EUR)", for example.
 */
export function buildMultiCurrencyBalanceDisplayName(
  fundingOption,
  isCFPBEnabledCountry,
  userAccountType
) {
  const { funding_sources } = fundingOption
  const currencyCodes = funding_sources.map(
    source => source.total_funds_in.currency_code
  )
  const formattedCurrencyCodes = currencyCodes.join(', ')
  return `${i18n(
    getCfpbContentKey(
      'fundingType.paypalBalance',
      isCFPBEnabledCountry,
      userAccountType
    )
  )} (${formattedCurrencyCodes})`
}

/* A failed lookup on properties files does not return
   an empty string nor undefined, but rather a function
   whose inner script is the failed lookup path
*/
export function safelyGetDynamicPropertiesValue(key) {
  let propertyValue = i18n(`fundingType.${key}`)
  if (typeof propertyValue === 'function' || !propertyValue) {
    return ''
  }

  return propertyValue
}

export function safelyGetDynamicPropertiesValueENRTR(key) {
  let propertyValue = enRTRStrings[`fundingType.${key}`]
  if (!propertyValue) {
    return ''
  }

  return propertyValue
}

/**
 * Funding source titles should be displayed as name + type + lastFour. This will handle any normal funding source.
 * e.g. Bank of America Checking x-1337
 */
export function buildFundingSourceDisplayName(
  source,
  isCFPBEnabledCountry,
  userAccountType,
  isCardArtEnabled
) {
  if (isCardArtEnabled) {
    return staticUtils.decodeEntities(
      `${source.choice_name ||
        getFundingSourceName(source, isCFPBEnabledCountry, userAccountType)}`
    )
  }
  let name = getFundingSourceName(source, isCFPBEnabledCountry, userAccountType)
  const shouldShowPaymentType =
    source.type && source.instrument_type !== INSTRUMENT_TYPES.CREDIT

  let type = get(source, 'type', '').toLowerCase()
  const paymentType = shouldShowPaymentType
    ? safelyGetDynamicPropertiesValue(type)
    : ''

  const lastFour = source.fi_last_4_content || ''
  return `${i18n('fundingType.displayName', {
    cardName: name,
    paymentType,
  })} ${lastFour}`
}

export function buildFundingSourceDisplayNameENRTR(
  source,
  isCFPBEnabledCountry,
  userAccountType,
  isCardArtEnabled
) {
  if (isCardArtEnabled) {
    return staticUtils.decodeEntities(
      `${source.choice_name ||
        getFundingSourceNameENRTR(
          source,
          isCFPBEnabledCountry,
          userAccountType
        )}`
    )
  }
  let name = getFundingSourceNameENRTR(
    source,
    isCFPBEnabledCountry,
    userAccountType
  )
  const shouldShowPaymentType =
    source.type && source.instrument_type !== INSTRUMENT_TYPES.CREDIT

  let type = get(source, 'type', '').toLowerCase()
  const paymentType = shouldShowPaymentType
    ? safelyGetDynamicPropertiesValueENRTR(type)
    : ''

  const lastFour = source.fi_last_4_content || ''
  return `${name} ${paymentType} ${lastFour}`
}

/**
 * Funding source account type should be displayed as
 * name
 * type + lastFour
 * Using names returned
 * e.g. Bank of America
 *      Checking x-1337
 */
export function buildFundingSourceAccountType(
  source,
  showENSubTextRTR = false
) {
  const shouldShowPaymentType =
    source.type && source.instrument_type !== INSTRUMENT_TYPES.CREDIT

  let type = get(source, 'type', '').toLowerCase()
  const propertyValue = showENSubTextRTR
    ? safelyGetDynamicPropertiesValueENRTR(type)
    : safelyGetDynamicPropertiesValue(type)
  const paymentType = shouldShowPaymentType ? propertyValue : ''

  const lastFour = source.fi_last_4_content || ''
  const providerLogoUrl = get(source, 'external_wallet.provider_logo_url')

  // If external wallet provider logo is passed, we will use that instead
  // ie: Chase Pay
  if (providerLogoUrl) {
    return (
      <div className={css(styles.marginFixProviderLogo)}>
        <img
          alt=""
          className={css(styles.externalProviderLogo)}
          src={providerLogoUrl}
        />
        <span className={css(styles.verticleLine)} />
        {paymentType} {lastFour}
      </div>
    )
  } else if (!providerLogoUrl) {
    return staticUtils.decodeEntities(`${paymentType} ${lastFour}`)
  }
}

/**
 *
 * If the user is making purchase transaction above 99 USD, using PayPal Credit as funding source,
 * based on the tags, showing if special offer is not applicable
 */
export function getPPCreditSpecialOfferText(source = {}) {
  /**
   * SRE bot's throws an error for dangerously set innerHTML. This is a workaround for
   * legitimate use cases
   * It is not a user input, it is for English content for locale values from enRTRStrings.json
   */
  const workaroundScoreBot = ['dangerously', 'Set', 'Inner', 'HTML'].join('')
  const setInnerHTML = key => ({
    [workaroundScoreBot]: {
      __html: key,
    },
  })

  if (
    source.ppcreditPromoFinancingOffer === PP_CREDIT_PROMO_STATE.NOT_APPLICABLE
  ) {
    return setInnerHTML(enRTRStrings['fundingType.ppCreditOfferNotApplicable'])
  } else if (
    source.ppcreditPromoFinancingOffer === PP_CREDIT_PROMO_STATE.ELIGIBLE
  ) {
    return setInnerHTML(enRTRStrings['fundingType.ppCreditOfferEligible'])
  } else if (
    source.ppcreditPromoFinancingOffer === PP_CREDIT_PROMO_STATE.INELIGIBLE
  ) {
    return setInnerHTML(enRTRStrings['fundingType.ppCreditOfferNotEligible'])
  }
  return null
}

export function getFundingSourceName(
  source = {},
  isCFPBEnabledCountry,
  userAccountType
) {
  switch (source.instrument_type) {
    case INSTRUMENT_TYPES.HOLDING:
      return getSingleBalanceName(source, isCFPBEnabledCountry, userAccountType)
    case INSTRUMENT_TYPES.PAYMENT_CARD:
      return getPaymentCardNetwork(source)
    case INSTRUMENT_TYPES.PAYMENT_TOKEN:
      return getPaymentTokenSourceName(source)
    case INSTRUMENT_TYPES.CREDIT:
      return getCreditName(source)
    case INSTRUMENT_TYPES.BANK_ACCOUNT:
      return source.bank_name
    default:
      return ''
  }
}

export function getFundingSourceNameENRTR(
  source = {},
  isCFPBEnabledCountry,
  userAccountType
) {
  switch (source.instrument_type) {
    case INSTRUMENT_TYPES.HOLDING:
      return getSingleBalanceNameENRTR(
        source,
        isCFPBEnabledCountry,
        userAccountType
      )
    case INSTRUMENT_TYPES.PAYMENT_CARD:
      return getPaymentCardNetworkENRTR(source)
    case INSTRUMENT_TYPES.PAYMENT_TOKEN:
      return getPaymentTokenSourceName(source)
    case INSTRUMENT_TYPES.CREDIT:
      return getCreditNameENRTR(source)
    case INSTRUMENT_TYPES.BANK_ACCOUNT:
      return source.bank_name
    default:
      return ''
  }
}

// if the funding source has reason as CIP required for holding instrument type, it should show up as unclaimed balance
export function getUnclaimedBalanceDisplayName(source) {
  const {
    total_funds_in: { amount },
  } = source

  return `${i18n('fundingType.unclaimedBalance', {
    amount,
  })}`
}

export function getUnclaimedBalanceDisplaySubText() {
  return i18n('fundingType.unclaimedBalanceSubText')
}

function getSingleBalanceName(source, isCFPBEnabledCountry, userAccountType) {
  const {
    total_funds_in: { currency_code },
  } = source
  return `${i18n(
    getCfpbContentKey(
      'fundingType.paypalBalance',
      isCFPBEnabledCountry,
      userAccountType
    )
  )} (${currency_code})`
}

function getSingleBalanceNameENRTR(
  source,
  isCFPBEnabledCountry,
  userAccountType
) {
  const {
    total_funds_in: { currency_code },
  } = source
  return `${
    enRTRStrings[
      getCfpbContentKey(
        'fundingType.paypalBalance',
        isCFPBEnabledCountry,
        userAccountType
      )
    ]
  } (${currency_code})`
}

export function getPaymentTokenSourceName(source) {
  switch (source.type) {
    case PAYMENT_TOKEN_TYPES.AMEX_P2P_PASS:
      return source.generic_issuer_name
    default:
      return source.type
  }
}

export function getPaymentCardNetwork(source) {
  switch (source.network) {
    case PAYMENT_CARDS.VISA:
      return i18n('fundingType.visa')
    case PAYMENT_CARDS.MASTERCARD:
    case PAYMENT_CARDS.MASTER_CARD:
      return i18n('fundingType.mastercard')
    case PAYMENT_CARDS.MAESTRO:
      return i18n('fundingType.maestro')
    case PAYMENT_CARDS.AMEX:
      return i18n('fundingType.amex')
    case PAYMENT_CARDS.DISCOVER:
      return i18n('fundingType.discover')
    case PAYMENT_CARDS.CHINA_UNION_PAY:
      return i18n('fundingType.china_union_pay')
    default:
      return source.network
  }
}

export function getPaymentCardNetworkENRTR(source) {
  switch (source.network) {
    case PAYMENT_CARDS.VISA:
      return enRTRStrings['fundingType.visa']
    case PAYMENT_CARDS.MASTERCARD:
    case PAYMENT_CARDS.MASTER_CARD:
      return enRTRStrings['fundingType.mastercard']
    case PAYMENT_CARDS.MAESTRO:
      return enRTRStrings['fundingType.maestro']
    case PAYMENT_CARDS.AMEX:
      return enRTRStrings['fundingType.amex']
    case PAYMENT_CARDS.DISCOVER:
      return enRTRStrings['fundingType.discover']
    case PAYMENT_CARDS.CHINA_UNION_PAY:
      return enRTRStrings['fundingType.china_union_pay']
    default:
      return source.network
  }
}

export function getCreditName(source) {
  switch (source.provider) {
    case CREDIT_PROVIDERS.PLUS_CARD:
      return i18n('fundingType.dualcardplus')
    case CREDIT_PROVIDERS.VIRTUAL_LINE:
      return i18n('fundingType.smartConnect')
    case CREDIT_PROVIDERS.EBAY_CARD:
      return i18n('fundingType.dualcardebay')
    case CREDIT_PROVIDERS.PAYPAL_CREDIT:
      return i18n('fundingType.paypalCredit')
    default:
      return source.provider
  }
}

export function getCreditNameENRTR(source) {
  switch (source.provider) {
    case CREDIT_PROVIDERS.PLUS_CARD:
      return enRTRStrings['fundingType.dualcardplus']
    case CREDIT_PROVIDERS.VIRTUAL_LINE:
      return enRTRStrings['fundingType.smartConnect']
    case CREDIT_PROVIDERS.EBAY_CARD:
      return enRTRStrings['fundingType.dualcardebay']
    case CREDIT_PROVIDERS.PAYPAL_CREDIT:
      return enRTRStrings['fundingType.paypalCredit']
    default:
      return source.provider
  }
}

export function buildBankDetails(
  selectedFundingOption,
  isCFPBEnabledCountry,
  userAccountType,
  isCardArtEnabled
) {
  const bankDetails = {}
  const fundingSource = staticUtils.getRelevantFundingSource(
    selectedFundingOption
  )
  const src = generateSourceLogoUrls(
    fundingSource,
    isCFPBEnabledCountry,
    userAccountType,
    isCardArtEnabled
  )
  const title = buildFundingSourceDisplayName(
    fundingSource,
    isCFPBEnabledCountry,
    userAccountType,
    isCardArtEnabled
  )
  const accountType = buildFundingSourceAccountType(fundingSource)

  bankDetails.src = src
  bankDetails.title = title
  bankDetails.accountType = accountType
  return bankDetails
}

/**
 * getFundingNameType is a helper function used currently to populate tracking data in some screens
 * @param {Object} selectedFundingOption - an object of selected funding option
 * @returns {{fi_name: string, fi_type: string}}
 */
export function getFundingNameType(selectedFundingOption = {}) {
  const { funding_sources = [] } = selectedFundingOption
  // only selected funding source is needed
  const funding_source = funding_sources[0] || {}

  const { instrument_type = 'noinstrument_type' } = funding_source

  // in case of PP balance generic_issuer_name/type are not populated
  if (instrument_type === 'HOLDING') {
    return {
      fi_name: 'PAYPAL',
      fi_type: 'BALANCE',
    }
  }

  const { type: fi_type = 'notype' } = funding_source
  const { generic_issuer_name: fi_name = 'noname' } = funding_source

  return {
    fi_name,
    fi_type,
  }
}
